"use client";

import { create } from "zustand";

// `/api/app-version` doubles as the connectivity probe and the update-check
// endpoint. `navigator.onLine` lies (false-negatives on VPNs, captive portals,
// after sleep — see MDN). Browser online/offline events are used only as
// *triggers* to re-probe sooner than the poll interval — their value is never read.

const PROBE_URL = "/api/app-version";
const PROBE_TIMEOUT_MS = 3_000;
const POLL_INTERVAL_MS = 10_000;
// Two adjacent failed probes collapse into one offline transition so a single
// transient blip doesn't flicker the banner. Online transitions are immediate.
const OFFLINE_DEBOUNCE_MS = 1_000;

// Shared Zustand store — one source of truth for every useIsOnline() consumer,
// for non-hook code that reads probe state via getState() (e.g. the TanStack
// Query retry callback in markets.ts), and for useLatestAppVersion().
export const onlineProbeState = create<{
  isOnline: boolean;
  latestVersion: string | null;
  _set: (v: boolean) => void;
  _setVersion: (v: string) => void;
}>((set) => ({
  // Start optimistically online — the probe overrides this within
  // PROBE_TIMEOUT_MS + OFFLINE_DEBOUNCE_MS. Avoids a false banner flash when
  // navigator.onLine is stale (VPN, captive portal, sleep wake).
  isOnline: true,
  latestVersion: null,
  _set: (isOnline) => set({ isOnline }),
  _setVersion: (latestVersion) => set({ latestVersion }),
}));

// ─── Singleton probe loop ─────────────────────────────────────────────────────
// Runs once per browser session regardless of how many useIsOnline() consumers
// exist, preventing N concurrent /api/app-version fetches when N components call
// useIsNetworkDown().

let inflight: Promise<{ online: boolean; version: string | null }> | null = null;
let offlineTimer: ReturnType<typeof setTimeout> | undefined;
let pollInterval: ReturnType<typeof setInterval> | undefined;

function runProbe() {
  if (inflight === null) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
    inflight = fetch(PROBE_URL, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
    })
      .then(async (res) => ({
        online: res.ok,
        version: res.ok ? await res.text() : null,
      }))
      .catch(() => ({ online: false, version: null }))
      .finally(() => {
        clearTimeout(timeoutId);
        inflight = null;
      });
  }
  void inflight.then(({ online, version }) => {
    const { isOnline, _set, _setVersion } = onlineProbeState.getState();
    if (version !== null) _setVersion(version);
    if (online) {
      clearTimeout(offlineTimer);
      offlineTimer = undefined;
      if (!isOnline) _set(true);
    } else if (isOnline && offlineTimer === undefined) {
      offlineTimer = setTimeout(() => {
        offlineTimer = undefined;
        _set(false);
      }, OFFLINE_DEBOUNCE_MS);
    }
  });
}

function startInterval() {
  if (pollInterval !== undefined) return;
  pollInterval = setInterval(runProbe, POLL_INTERVAL_MS);
}

function stopInterval() {
  if (pollInterval !== undefined) {
    clearInterval(pollInterval);
    pollInterval = undefined;
  }
}

function syncOnline() {
  if (document.visibilityState === "visible") {
    runProbe();
    startInterval();
  } else {
    stopInterval();
  }
}

function syncOffline() {
  // Probe immediately on the offline hint so we don't wait up to 10s for
  // the next interval tick. Keep the interval running — navigator.onLine is
  // not trusted, so there may be no subsequent online event to restart it
  // (e.g. VPN reconnect scenarios).
  runProbe();
}

// Initialize once per browser session on first module import.
if (typeof window !== "undefined") {
  syncOnline();
  document.addEventListener("visibilitychange", syncOnline);
  window.addEventListener("online", syncOnline);
  window.addEventListener("offline", syncOffline);
}

// ─── Public hooks ─────────────────────────────────────────────────────────────

export function useIsOnline(): boolean {
  return onlineProbeState((s) => s.isOnline);
}

export function useLatestAppVersion(): string | null {
  return onlineProbeState((s) => s.latestVersion);
}
